import { ridSegment } from '@krp/client-runtime'

/**
 * Адреса службы Resource.Aqsi (`/api/aqsi/v1/resources/{rid}/…`). rid —
 * сегмент пути каждого запроса (D-064): адрес без ресурса построить нельзя.
 * Литерал `/api/` законен только в файлах `api/urls.ts` (eslint).
 */
const resource = (resourceId: string) => `/api/aqsi/v1/resources/${ridSegment(resourceId)}`
const segment = (value: string) => encodeURIComponent(value)
/** RP-C9: касса кабинета (`deviceId`) — единственный адрес работы за кассой; локальных рабочих мест больше нет. */
const register = (resourceId: string, deviceId: number) =>
  `${resource(resourceId)}/pos/registers/${encodeURIComponent(String(deviceId))}`

export const AQSI_URLS = {
  cashierAccessVerify: '/api/cashiers/v1/verify',
  cashierRmkCurrent: '/api/cashiers/v1/rmk-settings/current',
  /** RP-CU4: кассиры нашей системы для входа в РМК — список рабочего места, вход по коду выбранного и пропуск на его кассира кабинета. */
  ownCashierWorkers: '/api/cashiers/v1/people/workplace',
  ownCashierSignIn: '/api/cashiers/v1/people/sign-in',
  ownCashierGrant: '/api/cashiers/v1/people/grants',
  // C2: снимки кабинета и проверка фискального профиля.
  goods: (resourceId: string) => `${resource(resourceId)}/catalog/products`,
  /** РМК: карточки снимка по ключам кассы, которые назвал общий каталог (`POST { keys }`, чтение). */
  goodsByKeys: (resourceId: string) => `${resource(resourceId)}/catalog/products/by-keys`,
  /** Окно товара источника: карточка кабинета живьём (GET) и правка карточки в кабинете (PUT, право `catalog.write`). */
  goodCard: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}/card`,
  /** ADR-21, RP-FB71: расширение карточки (группа, свойства, тип…) из снимка кабинета, без обращения к кабинету. */
  goodExtension: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}/extension`,
  goodEdit: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}`,
  /** Задать цену карточки кабинета (право `prices.write`). */
  goodPrice: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}/price`,
  /** RP-FB39: цены карточки живьём (цена и магазины) и цена в магазине. */
  goodPrices: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}/prices`,
  goodShopPrice: (resourceId: string, goodId: string) => `${resource(resourceId)}/catalog/products/${segment(goodId)}/shop-price`,
  /** RP-SE12: отдельный индекс единой службы поиска по снимку кабинета (вкладка «Каталог AQSI»). */
  probeStatus: (resourceId: string) => `${resource(resourceId)}/catalog/probe-status`,
  probeRebuild: (resourceId: string) => `${resource(resourceId)}/catalog/probe-rebuild`,
  probeSearch: (resourceId: string) => `${resource(resourceId)}/catalog/probe-search`,
  /** RP-SE13: свежесть каталога и «Проверить актуальность» (проба; `?full=true` - полная сверка). */
  freshness: (resourceId: string) => `${resource(resourceId)}/catalog/freshness`,
  freshnessCheck: (resourceId: string) => `${resource(resourceId)}/catalog/freshness/check`,
  taxSummary: (resourceId: string) => `${resource(resourceId)}/catalog/tax-summary`,
  /** RP-SYN12: «Создание по умолчанию» — шаблон создания карточки с умолчаниями ресурса (GET) и их сохранение (PUT, право `catalog.write`). */
  createDefaults: (resourceId: string) => `${resource(resourceId)}/sync/create-defaults`,
  /** RP-SE13: сигнал «я в системе» - первый после тишины запускает одну проверку кабинетов. Не про ресурс. */
  presence: () => '/api/aqsi/v1/presence',
  catalogRefresh: (resourceId: string) => `${resource(resourceId)}/catalog/refresh`,
  devices: (resourceId: string) => `${resource(resourceId)}/devices`,
  cashiers: (resourceId: string) => `${resource(resourceId)}/cashiers`,
  devicesRefresh: (resourceId: string) => `${resource(resourceId)}/devices/refresh`,
  /** FIX-C3: проверка связи с кассой и связь касс в диагностике. */
  deviceCheck: (resourceId: string, deviceId: number) => `${resource(resourceId)}/devices/${String(deviceId)}/check`,
  devicesConnection: (resourceId: string) => `${resource(resourceId)}/devices/connection`,
  fiscalCheck: (resourceId: string) => `${resource(resourceId)}/fiscal-profile/check`,

  // RP-C8/C9: кассы кабинета, работа за кассой (сессии), смены, связь двух уровней.
  registers: (resourceId: string) => `${resource(resourceId)}/pos/registers`,
  posCashiers: (resourceId: string) => `${resource(resourceId)}/pos/cashiers`,
  /** RP-FB22 4.2: проверка кода кассира; код уходит одним запросом, ответ — подтверждение со сроком. */
  cashierVerify: (resourceId: string, cashierId: string) =>
    `${resource(resourceId)}/pos/cashiers/${segment(cashierId)}/verify`,
  sessionMine: (resourceId: string) => `${resource(resourceId)}/sessions/mine`,
  session: (resourceId: string, deviceId: number, action: SessionAction) =>
    `${register(resourceId, deviceId)}/session/${action}`,
  sessionJournal: (resourceId: string, deviceId: number) => `${register(resourceId, deviceId)}/session/journal`,
  printSettings: (resourceId: string, deviceId: number) => `${register(resourceId, deviceId)}/print-settings`,
  shift: (resourceId: string, deviceId: number) => `${register(resourceId, deviceId)}/shift`,
  shiftCommand: (resourceId: string, deviceId: number, action: ShiftAction) =>
    `${register(resourceId, deviceId)}/shift/${action}`,
  /** С3: связь двух уровней — сервер AQSI и аппарат кассы; `link/check` — команда кассе, только по явному вызову. */
  link: (resourceId: string, deviceId: number) => `${register(resourceId, deviceId)}/link`,
  linkCheck: (resourceId: string, deviceId: number) => `${register(resourceId, deviceId)}/link/check`,
  operation: (resourceId: string, operationId: string) => `${resource(resourceId)}/operations/${segment(operationId)}`,
  operationReconcile: (resourceId: string, operationId: string) =>
    `${resource(resourceId)}/operations/${segment(operationId)}/reconcile`,
  // UI-ACTIONS P1 №5: ручная сверка команды смены — тот же адрес, что у операций продажи.
  operationResolve: (resourceId: string, operationId: string) =>
    `${resource(resourceId)}/operations/${segment(operationId)}/resolve`,
  /** RP-FB11: виды цен продажи для кассы — общий каталог Platform (право кассы `session.open`). */
  priceTypes: () => '/api/platform/v1/catalog/price-types',
} as const

export type SessionAction = 'open' | 'heartbeat' | 'close' | 'takeover' | 'close-foreign' | 'price-type'
export type ShiftAction = 'adopt' | 'open' | 'close'
